import { useEffect, useState, type ReactNode } from 'react'
import BentoCard from './BentoCard'
import MaterialIcon from './MaterialIcon'
import { KILO } from '../../constants/KILO_THEME'

type CoachSectionCardProps = {
  title: string
  icon?: string
  badge?: number
  defaultExpanded?: boolean
  expanded?: boolean
  onExpandedChange?: (expanded: boolean) => void
  teaser?: string | null
  featured?: boolean
  contentClassName?: string
  children: ReactNode
  onExpand?: () => void
}

const CoachSectionCard = ({
  title,
  icon,
  badge = 0,
  defaultExpanded = true,
  expanded: expandedProp,
  onExpandedChange,
  teaser,
  featured = false,
  contentClassName = 'px-5 pb-5',
  children,
  onExpand,
}: CoachSectionCardProps) => {
  const [internalExpanded, setInternalExpanded] = useState(defaultExpanded)
  const expanded = expandedProp ?? internalExpanded

  useEffect(() => {
    if (expandedProp === undefined) {
      setInternalExpanded(defaultExpanded)
    }
  }, [defaultExpanded, expandedProp])

  const toggle = () => {
    const next = !expanded
    if (expandedProp === undefined) setInternalExpanded(next)
    onExpandedChange?.(next)
    if (next) onExpand?.()
  }

  return (
    <BentoCard gradient={featured ? 'forest' : 'none'} featured={featured} className="!p-0 overflow-hidden">
      <button
        type="button"
        onClick={toggle}
        className="flex w-full items-center gap-3 px-5 py-4 text-left transition-colors hover:bg-kilo-text/[0.02]"
        aria-expanded={expanded}
      >
        {icon ? <MaterialIcon name={icon} hoverable size="sm" className="shrink-0" /> : null}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <p className={KILO.meta}>{title}</p>
            {badge > 0 ? (
              <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-kilo-accent px-1.5 font-mono text-[10px] font-bold text-black">
                {badge > 9 ? '9+' : badge}
              </span>
            ) : null}
          </div>
          {!expanded && teaser ? (
            <p className={`mt-1 truncate text-sm leading-relaxed ${KILO.text.muted}`}>{teaser}</p>
          ) : null}
        </div>
        <MaterialIcon
          name={expanded ? 'expand_less' : 'expand_more'}
          size="sm"
          className="shrink-0"
        />
      </button>
      {expanded ? <div className={`divide-y divide-kilo-border ${contentClassName}`}>{children}</div> : null}
    </BentoCard>
  )
}

export default CoachSectionCard
